import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useRemountOnPageHidden } from '../../hooks/useRemountOnPageHidden';
import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  useIonRouter,
  IonPage,
  IonGrid,
  IonRow,
  IonCol,
  IonAvatar,
  IonModal,
  IonItem,
  IonLabel,
  IonText,
  IonList,
  IonBadge,
} from '@ionic/react';
import {
  cashOutline,
  briefcaseOutline,
  notificationsOutline,
  calendarOutline,
  person,
  timerOutline,
  pencil,
  locationOutline,
  timeOutline,
  checkmarkCircleOutline,
  trendingUpOutline,
} from 'ionicons/icons';
import { useAuth } from '../../contexts';
import styles from './DashboardMLS.module.scss';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { Shortcut } from '@rsmaids/shared/types/dashboard';
import { MLS_SHORTCUTS, MLS_CATEGORY_NAMES } from './shortcutCatalogues';
import { ShortcutsEditModal } from '../../components/ShortcutsEditModal';
import BookingDetails from '../bookingSystem/BookingDetails';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { googleCalendarService } from '../../services/googleCalendarService';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../i18n/locale';

interface EmployeeStats {
  todayJobs: number;
  weekJobs: number;
  monthHours: number;
  monthPay: number;
}

interface TodayJob {
  id: string;
  title: string;
  time: string;
  location: string;
  clientName: string;
  duration: string;
}

interface RecentActivity {
  id: string;
  type: 'job_completed' | 'message' | 'update';
  title: string;
  description: string;
  timestamp: Date;
  icon: string;
}

interface UpcomingJob {
  id: string;
  date: string;
  dayOfWeek: string;
  jobs: Array<{
    id: string;
    time: string;
    title: string;
    clientName: string;
  }>;
}

const DashboardMLS: React.FC = () => {
  const { t } = useTranslation();
  // The scrolling area is rebuilt while the page is hidden, so it is back at the top on return
  const scrollAreaRef = useRef<HTMLIonContentElement>(null);
  const scrollResetKey = useRemountOnPageHidden(scrollAreaRef);
  const router = useIonRouter();
  // Badge = notifications that arrived since the alerts were last opened (from the
  // context's already-loaded newest notifications — no separate fetch or polling).
  const { authState, unreadCount: unreadNotificationsCount } = useAuth();
  const { user } = authState;

  const [stats, setStats] = useState<EmployeeStats>({
    todayJobs: 0,
    weekJobs: 0,
    monthHours: 0,
    monthPay: 0,
  });
  const [todayJobs, setTodayJobs] = useState<TodayJob[]>([]);
  const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [upcomingWeek, setUpcomingWeek] = useState<UpcomingJob[]>([]);

  // Clock state (state values prefixed with _ to avoid unused-var lint warning)
  const [_currentTimeEntry, setCurrentTimeEntry] = useState<Record<string, unknown> | null>(null);
  const [_isClockedIn, setIsClockedIn] = useState<boolean>(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  // Reads localStorage directly in the lazy initializer (not via a mount
  // effect) since this only ever needs to happen once, before first paint.
  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(() => {
    const savedShortcuts = localStorage.getItem('dashboard-employee-shortcuts');
    if (savedShortcuts) {
      try {
        const parsedShortcuts = JSON.parse(savedShortcuts);
        if (Array.isArray(parsedShortcuts) && parsedShortcuts.length > 0) {
          return parsedShortcuts;
        }
      } catch (error) {
        console.error('Error parsing saved shortcuts:', error);
      }
    }
    return ['schedule', 'timesheet', 'pay', 'profile'];
  });

  // Shortcut management
  const allShortcuts: Shortcut[] = MLS_SHORTCUTS;
  const categoryNames = MLS_CATEGORY_NAMES;

  const activeShortcuts = activeShortcutIds
    .map(id => allShortcuts.find(s => s.id === id))
    .filter((shortcut): shortcut is Shortcut => shortcut !== undefined);

  const toggleEditMode = useCallback(() => {
    setIsEditMode(!isEditMode);
  }, [isEditMode]);

  // Load stats
  useEffect(() => {
    // Signed out (or signing out): nothing to load, and the calls below would all fail with "User must be signed in"
    if (!user) return;
    let cancelled = false;

    const loadStats = async () => {
      try {
        const dataService = DataServiceFactory.getService();
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        const staffName = user?.name || user?.firstName || '';

        // Fetch today's bookings — use overlap-based query with timezone buffer (same as MySchedule)
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        const todayBookings = await dataService.bookings.getBookingsAndBlockedSlotsByDateRange(today, todayEnd, []);
        if (cancelled) return;
        const myTodayJobs = (todayBookings as BookingItem[]).filter((b: BookingItem) => {
          const startTime = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
          const isToday = startTime.getFullYear() === today.getFullYear() &&
            startTime.getMonth() === today.getMonth() &&
            startTime.getDate() === today.getDate();
          return isToday &&
            (!staffName || (b.staff && b.staff.toLowerCase().includes(staffName.toLowerCase()))) &&
            b.status?.toLowerCase() !== 'cancelled';
        });

        // Store full booking data so BookingDetails can open without a re-fetch
        myTodayJobs.forEach((b: BookingItem) => {
          if (b.id) DataStorageService.storeData(`booking-${b.id}`, b);
        });

        const formattedTodayJobs: TodayJob[] = myTodayJobs
          .sort((a: BookingItem, b: BookingItem) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
          )
          .map((b: BookingItem) => {
            const startTime = b.startTime instanceof Date
              ? b.startTime.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })
              : typeof b.startTime === 'string'
                ? new Date(b.startTime).toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })
                : 'TBD';

            return {
              id: b.id || '',
              title: b.title || t('pages.dashboard.DashboardMLS.cleaningService'),
              time: startTime,
              location: b.location || b.contact?.address || 'Location TBD',
              clientName: b.contact?.name || 'Client',
              duration: b.duration ? `${b.duration}h` : '',
            };
          });

        // Fetch week's Wix bookings (all non-cancelled, not just completed)
        const weekEndDate = new Date(weekStart);
        weekEndDate.setDate(weekStart.getDate() + 6);
        weekEndDate.setHours(23, 59, 59, 999);
        const weekBookings = await dataService.bookings.getBookingsAndBlockedSlotsByDateRange(weekStart, weekEndDate, []);
        if (cancelled) return;
        const wixWeekCount = (weekBookings as BookingItem[]).filter((b: BookingItem) =>
          (!staffName || (b.staff && b.staff.toLowerCase().includes(staffName.toLowerCase()))) &&
          b.status?.toLowerCase() !== 'cancelled' &&
          b.status?.toLowerCase() !== 'canceled'
        ).length;

        // Fetch Google Calendar events for today and this week
        let gcalTodayJobs: TodayJob[] = [];
        let gcalWeekCount = 0;
        try {
          const gcalWeekResult = await googleCalendarService.getEvents(weekStart, weekEndDate);
          if (gcalWeekResult.connected && !gcalWeekResult.tokenRevoked) {
            gcalWeekCount = gcalWeekResult.events.filter((e) => {
              const s = new Date(e.start);
              const isInWeek = s >= weekStart && s <= weekEndDate;
              // Company job invites: user was invited by someone else (organizerSelf = false)
              const isJobInvite = e.organizerSelf === false;
              return isInWeek && isJobInvite;
            }).length;
          }
          const gcalResult = { connected: gcalWeekResult.connected, tokenRevoked: gcalWeekResult.tokenRevoked, events: (gcalWeekResult.events || []).filter((e) => { const s = new Date(e.start); return s >= today && s <= todayEnd; }) };
          if (gcalResult.connected && !gcalResult.tokenRevoked) {
            gcalTodayJobs = gcalResult.events
              .filter((e) => {
                const start = new Date(e.start);
                return start.getFullYear() === today.getFullYear() &&
                  start.getMonth() === today.getMonth() &&
                  start.getDate() === today.getDate();
              })
              .map((e) => {
                const startDate = new Date(e.start);
                const endDate = new Date(e.end);
                const durationMs = endDate.getTime() - startDate.getTime();
                const durationMins = Math.round(durationMs / 60000);
                const durationStr = e.isAllDay
                  ? ''
                  : durationMins >= 60
                    ? `${Math.floor(durationMins / 60)}h${durationMins % 60 > 0 ? ` ${durationMins % 60}m` : ''}`
                    : `${durationMins}m`;
                // Don't show email addresses as calendar name
                const calName = e.calendarName && !e.calendarName.includes('@') ? e.calendarName : 'Google Calendar';
                return {
                  id: e.id,
                  title: e.title,
                  time: e.isAllDay ? 'All day' : startDate.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true }),
                  location: e.location || '',
                  clientName: calName,
                  duration: durationStr,
                };
              });
          }
        } catch {
          // gcal unavailable — skip silently
        }
        if (cancelled) return;

        setTodayJobs([...formattedTodayJobs, ...gcalTodayJobs].sort((a, b) => {
          if (a.time === 'All day') return -1;
          if (b.time === 'All day') return 1;
          return new Date(`1970/01/01 ${a.time}`).getTime() - new Date(`1970/01/01 ${b.time}`).getTime();
        }));

        // Monthly stats — completed jobs only for pay/hours calculation
        const monthBookings = await dataService.bookings.getBookingsByDateRange(monthStart, monthEnd);
        if (cancelled) return;
        const myCompletedJobs = monthBookings.filter((b: BookingItem) =>
          (!staffName || (b.staff && b.staff.toLowerCase().includes(staffName.toLowerCase()))) &&
          b.status?.toLowerCase() === 'completed'
        );

        const myJobs = myCompletedJobs;

        const monthHours = myJobs.reduce((total: number, b: BookingItem) => {
          const raw = b.duration ?? '0';
          const str = typeof raw === 'number' ? String(raw) : raw;
          const parsed = parseFloat(str.replace('h', '')) || 0;
          return total + parsed;
        }, 0);

        const hourlyRate = 18.50;
        const monthPay = monthHours * hourlyRate;

        // Create recent activity from completed jobs
        const recentCompletedJobs = myJobs
          .sort((a: BookingItem, b: BookingItem) => {
            const dateA = new Date(a.date || '').getTime();
            const dateB = new Date(b.date || '').getTime();
            return dateB - dateA;
          })
          .slice(0, 5)
          .map((b: BookingItem): RecentActivity => ({
            id: b.id || '',
            type: 'job_completed',
            title: t('pages.dashboard.DashboardMLS.completed', { v1: b.title || 'Cleaning Service' }),
            description: `${b.contact?.name || 'Client'} • ${b.location || b.contact?.address || 'Location'}`,
            timestamp: new Date(b.date || ''),
            icon: checkmarkCircleOutline,
          }));

        setRecentActivity(recentCompletedJobs);

        // Get upcoming week schedule (next 7 days excluding today)
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() + 7);

        const upcomingBookings = await dataService.bookings.getBookingsByDateRange(tomorrow, weekEnd);
        if (cancelled) return;
        const myUpcomingJobs = upcomingBookings.filter((b: BookingItem) =>
          b.staff && b.staff.includes(staffName) && b.status?.toLowerCase() !== 'cancelled'
        );

        // Group by date
        const jobsByDate = new Map<string, BookingItem[]>();
        myUpcomingJobs.forEach((job: BookingItem) => {
          const dateKey = job.date;
          if (!jobsByDate.has(dateKey)) {
            jobsByDate.set(dateKey, []);
          }
          jobsByDate.get(dateKey)!.push(job);
        });

        // Format for display
        const upcomingSchedule: UpcomingJob[] = Array.from(jobsByDate.entries())
          .map(([dateStr, jobs]) => {
            const date = new Date(dateStr);
            return {
              id: dateStr,
              date: date.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' }),
              dayOfWeek: date.toLocaleDateString(intlLocale(), { weekday: 'short' }),
              jobs: jobs.map((b: BookingItem) => {
                const startTime = b.startTime instanceof Date
                  ? b.startTime.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })
                  : typeof b.startTime === 'string'
                    ? b.startTime
                    : 'TBD';
                return {
                  id: b.id || '',
                  time: startTime,
                  title: b.title || t('pages.dashboard.DashboardMLS.cleaningService'),
                  clientName: b.contact?.name || 'Client',
                };
              }).sort((a, b) => a.time.localeCompare(b.time)),
            };
          })
          .sort((a, b) => new Date(a.id).getTime() - new Date(b.id).getTime())
          .slice(0, 5);

        setUpcomingWeek(upcomingSchedule);

        const finalStats = {
          todayJobs: formattedTodayJobs.length + gcalTodayJobs.length,
          weekJobs: wixWeekCount + gcalWeekCount,
          monthHours: Math.round(monthHours),
          monthPay: Math.round(monthPay * 100) / 100,
        };

        setStats(finalStats);
      } catch (error) {
        // A sign-out in the middle of loading is expected, not an error
        if (!cancelled) console.error('Error loading stats:', error);
      }
    };

    loadStats();
    return () => { cancelled = true; };
  }, [user, t]);

  // Load clock state — computed during render (React's "adjust state when
  // a dependency changes" pattern) rather than as setState calls inside an
  // effect, since it's a pure synchronous localStorage read with no async
  // work involved.
  const [prevUserForClockState, setPrevUserForClockState] = useState(user);
  if (prevUserForClockState !== user) {
    setPrevUserForClockState(user);
    const savedEntry = localStorage.getItem('current-time-entry');
    if (savedEntry) {
      try {
        const entry = JSON.parse(savedEntry);
        setCurrentTimeEntry(entry);
        setIsClockedIn(true);
      } catch {
        localStorage.removeItem('current-time-entry');
      }
    }
  }


  // helper (prefixed with _ to avoid unused-var ESLint warning)
  const _formatTime = (timeString: string) => {
    const time = new Date(`2023-01-01T${timeString}`);
    return time.toLocaleString(intlLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <IonPage>
      <IonHeader mode="ios" className="ion-no-border">
        <IonToolbar className={styles.dashboardToolbar}>
          <IonButton
            slot="start"
            fill="clear"
            className={styles.avatarButton}
            onClick={() => router.push('/tabs/profile')}
            title={t('pages.dashboard.DashboardMLS.viewProfile')}
            aria-label={t('pages.dashboard.DashboardMLS.viewProfile')}
          >
            <IonAvatar slot="icon-only" className={styles.userAvatar}>
              {user?.avatarUrl ? (
                <img
                  src={proxyImage(user.avatarUrl)}
                  alt={user?.name || ''}
                  onError={(e) => {
                    const imgElement = e.target as HTMLImageElement;
                    imgElement.src = getDefaultAvatar(user?.name || user?.email || 'User');
                  }}
                />
              ) : (
                <img
                  src={getDefaultAvatar(user?.name || user?.email || 'User')}
                  alt={user?.name || t('pages.dashboard.DashboardMLS.user')}
                />
              )}
            </IonAvatar>
          </IonButton>
          <IonLabel>
            <h3 className={styles.welcomeText}>{t('pages.dashboard.DashboardMLS.welcomeBack')}</h3>
            <h2 className={styles.userName}>
              {user?.firstName || (user?.name ? user.name.split(' ')[0] : t('pages.dashboard.DashboardMLS.employee'))}
            </h2>
          </IonLabel>
          <IonButtons slot="end">
            <IonItem lines="none" className={styles.alertsButtonWrapper}>
              <IonButton
                shape='round'
                className={styles.headerIconButton}
                onClick={() => router.push('/tabs/dashboard/mls/alerts')}
                title={t('pages.dashboard.DashboardMLS.alerts')}
                aria-label={t('pages.dashboard.DashboardMLS.alerts')}
              >
                <IonIcon icon={notificationsOutline} slot="icon-only" />
              </IonButton>
              {unreadNotificationsCount > 0 && (
                <IonBadge color="danger" className={styles.notificationBadge}>
                  {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                </IonBadge>
              )}
            </IonItem>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonGrid className={styles.dashboardContainer}>
          {/* Menu Cards - native horizontal scroll (see .scrollContainer) */}
          <div className={styles.menuWrapper}>
          <IonRow className={styles.scrollContainer}>
            {/* Edit button */}
            <IonButton
              fill="clear"
              className={`${styles.menuCard} ${styles.editButton}`}
              onClick={toggleEditMode}
            >
              <IonLabel className={styles.menuCardContent}>
                <IonIcon icon={pencil} className={styles.menuIcon} />
              </IonLabel>
            </IonButton>

            {/* Dynamic shortcuts */}
            {activeShortcuts.map((shortcut) => (
              <IonButton
                key={shortcut.id}
                fill="clear"
                className={styles.menuCard}
                onClick={() => router.push(shortcut.route)}
              >
                <IonLabel className={styles.menuCardContent}>
                  <IonIcon icon={shortcut.icon} className={styles.menuIcon} />
                  <IonText className={styles.menuTitle}>{shortcut.title}</IonText>
                </IonLabel>
              </IonButton>
            ))}
          </IonRow>
        </div>

          {/* Notches element */}
          <div className={styles.menuNotches}></div>

          {/* All content below menu becomes scrollable */}
          <IonContent key={scrollResetKey} ref={scrollAreaRef} className={styles.scrollableDashboard}>
            {/* Today's Schedule */}
            <IonCard className={styles.scheduleCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.cardTitle}>{t('pages.dashboard.DashboardMLS.todaySSchedule')}
                  {stats.todayJobs > 0 && (
                    <IonBadge color="primary" className={styles.jobCountBadge}>
                      {stats.todayJobs}
                    </IonBadge>
                  )}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {todayJobs.length > 0 ? (
                  <IonList className={styles.jobsList} lines="none">
                    {todayJobs.map((job) => (
                      <IonItem key={job.id} className={styles.jobItem} onClick={() => { setSelectedBookingId(job.id); setShowBookingDetails(true); }} button detail={false}>
                        <IonLabel>
                          <IonGrid className={styles.jobHeader}>
                            <IonRow className="ion-justify-content-between ion-align-items-center">
                              <IonCol size="auto">
                                <IonText className={styles.jobTime}>
                                  <IonIcon icon={timeOutline} className={styles.jobTimeIcon} />
                                  <span>{job.time}</span>
                                </IonText>
                              </IonCol>
                              {job.duration ? (
                                <IonCol size="auto">
                                  <IonText className={styles.jobDuration}>{job.duration}</IonText>
                                </IonCol>
                              ) : null}
                            </IonRow>
                          </IonGrid>
                          <IonText className={styles.jobTitle}>{job.title}</IonText>
                          <IonGrid className={styles.jobDetails}>
                            <IonRow className={styles.jobDetail}>
                              <IonCol size="auto" className="ion-no-padding">
                                <IonIcon icon={person} />
                              </IonCol>
                              <IonCol>
                                <span>{job.clientName}</span>
                              </IonCol>
                            </IonRow>
                            <IonRow className={styles.jobDetail}>
                              <IonCol size="auto" className="ion-no-padding">
                                <IonIcon icon={locationOutline} />
                              </IonCol>
                              <IonCol>
                                <span>{job.location}</span>
                              </IonCol>
                            </IonRow>
                          </IonGrid>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                ) : (
                  <IonGrid className={styles.emptySchedule}>
                    <IonRow className="ion-justify-content-center">
                      <IonCol size="12" className="ion-text-center">
                        <IonIcon icon={calendarOutline} className={styles.emptyIcon} />
                        <IonText className={styles.emptyText}>{t('pages.dashboard.DashboardMLS.noJobsScheduledToday')}</IonText>
                        <IonText className={styles.emptySubtext}>{t('pages.dashboard.DashboardMLS.enjoyYourDayOff')}</IonText>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                )}
              </IonCardContent>
            </IonCard>

            {/* Quick Stats Grid */}
            <IonGrid className={styles.statsGrid}>
              <IonRow>
                <IonCol size="6">
                  <IonCard className={styles.statCard} onClick={() => router.push('/tabs/my-schedule')} button>
                    <IonCardContent>
                      <IonGrid className="ion-no-padding">
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto" className={styles.statCardIcon}>
                            <IonIcon icon={briefcaseOutline} />
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardValue}>{stats.weekJobs}</IonText>
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardLabel}>{t('pages.dashboard.DashboardMLS.jobsThisWeek')}</IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
                <IonCol size="6">
                  <IonCard className={styles.statCard} onClick={() => router.push('/tabs/profile')} button>
                    <IonCardContent>
                      <IonGrid className="ion-no-padding">
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto" className={styles.statCardIcon}>
                            <IonIcon icon={cashOutline} />
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardValue}>${stats.monthPay}</IonText>
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardLabel}>{t('pages.dashboard.DashboardMLS.monthEarnings')}</IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="6">
                  <IonCard className={styles.statCard} button>
                    <IonCardContent>
                      <IonGrid className="ion-no-padding">
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto" className={styles.statCardIcon}>
                            <IonIcon icon={timerOutline} />
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardValue}>{stats.monthHours}h</IonText>
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardLabel}>{t('pages.dashboard.DashboardMLS.hoursThisMonth')}</IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
                <IonCol size="6">
                  <IonCard className={styles.statCard} button>
                    <IonCardContent>
                      <IonGrid className="ion-no-padding">
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto" className={styles.statCardIcon}>
                            <IonIcon icon={trendingUpOutline} />
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardValue}>{recentActivity.length}</IonText>
                          </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                          <IonCol size="auto">
                            <IonText className={styles.statCardLabel}>{t('pages.dashboard.DashboardMLS.completedJobs')}</IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
            </IonGrid>

            {/* Upcoming This Week */}
            {upcomingWeek.length > 0 && (
              <IonCard className={styles.upcomingCard}>
                <IonCardHeader>
                  <IonCardTitle className={styles.cardTitle}>{t('pages.dashboard.DashboardMLS.upcomingThisWeek')}
                    <IonBadge color="secondary" className={styles.jobCountBadge}>
                      {upcomingWeek.reduce((sum, day) => sum + day.jobs.length, 0)}
                    </IonBadge>
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList className={styles.upcomingList} lines="none">
                    {upcomingWeek.map((day) => (
                      <IonItem key={day.id} className={styles.upcomingDay} lines="none">
                        <IonLabel>
                          <IonGrid className={styles.upcomingDayHeader}>
                            <IonRow className="ion-justify-content-between ion-align-items-center">
                              <IonCol size="auto" className={styles.upcomingDayDate}>
                                <IonText className={styles.dayOfWeek}>{day.dayOfWeek}</IonText>
                                <IonText className={styles.dateNum}>{day.date}</IonText>
                              </IonCol>
                              <IonCol size="auto">
                                <IonBadge className={styles.upcomingDayCount}>
                                  {day.jobs.length} {day.jobs.length === 1 ? 'job' : 'jobs'}
                                </IonBadge>
                              </IonCol>
                            </IonRow>
                          </IonGrid>
                          <IonList className={styles.upcomingDayJobs} lines="none">
                            {day.jobs.map((job) => (
                              <IonItem key={job.id} className={styles.upcomingJobItem} onClick={() => router.push('/tabs/my-schedule')} button detail={false}>
                                <IonLabel>
                                  <IonGrid>
                                    <IonRow className="ion-align-items-center">
                                      <IonCol size="auto">
                                        <IonText className={styles.upcomingJobTime}>{job.time}</IonText>
                                      </IonCol>
                                      <IonCol className={styles.upcomingJobInfo}>
                                        <IonText className={styles.upcomingJobTitle}>{job.title}</IonText>
                                        <IonText className={styles.upcomingJobClient}>{job.clientName}</IonText>
                                      </IonCol>
                                    </IonRow>
                                  </IonGrid>
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <IonCard className={styles.activityCard}>
                <IonCardHeader>
                  <IonCardTitle className={styles.cardTitle}>
                    <IonIcon icon={checkmarkCircleOutline} className={styles.titleIcon} />{t('pages.dashboard.DashboardMLS.recentCompletions')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList className={styles.activityList} lines="none">
                    {recentActivity.slice(0, 5).map((activity) => (
                      <IonItem key={activity.id} className={styles.activityItem} lines="full">
                        <IonAvatar slot="start" className={styles.activityIconWrapper}>
                          <IonIcon icon={activity.icon} className={styles.activityIcon} />
                        </IonAvatar>
                        <IonLabel className={styles.activityContent}>
                          <IonText className={styles.activityTitle}>{activity.title}</IonText>
                          <IonText className={styles.activityDescription}>{activity.description}</IonText>
                        </IonLabel>
                        <IonText slot="end" className={styles.activityTime}>
                          {activity.timestamp.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' })}
                        </IonText>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {/* Quick Actions */}
            <IonCard className={styles.quickActionsCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.cardTitle}>{t('pages.dashboard.DashboardMLS.quickActions')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList className={styles.quickActionsList} lines="none">
                  <IonItem className={styles.quickActionButton} onClick={() => router.push('/tabs/my-schedule')} button detail={false} lines="full">
                    <IonAvatar slot="start" className={styles.quickActionIcon}>
                      <IonIcon icon={calendarOutline} />
                    </IonAvatar>
                    <IonLabel className={styles.quickActionText}>
                      <IonText className={styles.quickActionTitle}>{t('pages.dashboard.DashboardMLS.viewFullSchedule')}</IonText>
                      <IonText className={styles.quickActionDesc}>{t('pages.dashboard.DashboardMLS.seeAllUpcomingJobs')}</IonText>
                    </IonLabel>
                  </IonItem>
                  <IonItem className={styles.quickActionButton} onClick={() => router.push('/tabs/profile')} button detail={false} lines="full">
                    <IonAvatar slot="start" className={styles.quickActionIcon}>
                      <IonIcon icon={cashOutline} />
                    </IonAvatar>
                    <IonLabel className={styles.quickActionText}>
                      <IonText className={styles.quickActionTitle}>{t('pages.dashboard.DashboardMLS.viewEarnings')}</IonText>
                      <IonText className={styles.quickActionDesc}>{t('pages.dashboard.DashboardMLS.checkPaymentDetails')}</IonText>
                    </IonLabel>
                  </IonItem>
                  <IonItem className={styles.quickActionButton} onClick={() => router.push('/tabs/dashboard/mls/alerts')} button detail={false} lines="none">
                    <IonAvatar slot="start" className={styles.quickActionIcon}>
                      <IonIcon icon={notificationsOutline} />
                    </IonAvatar>
                    <IonLabel className={styles.quickActionText}>
                      <IonText className={styles.quickActionTitle}>{t('pages.dashboard.DashboardMLS.viewNotifications')}</IonText>
                      <IonText className={styles.quickActionDesc}>
                        {unreadNotificationsCount > 0
                          ? `${unreadNotificationsCount} new message${unreadNotificationsCount === 1 ? '' : 's'}`
                          : t('pages.dashboard.DashboardMLS.allCaughtUp')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
          </IonContent>

          {/* Edit Shortcuts Modal */}
          <IonModal
            isOpen={isEditMode}
            onDidDismiss={() => setIsEditMode(false)}
            className={styles.editModal}
          >
            <ShortcutsEditModal
              onDismiss={() => setIsEditMode(false)}
              activeShortcutIds={activeShortcutIds}
              allShortcuts={allShortcuts}
              categoryNames={categoryNames}
              onSave={async (shortcuts) => {
                setActiveShortcutIds(shortcuts);
                localStorage.setItem('dashboard-employee-shortcuts', JSON.stringify(shortcuts));
              }}
            />
          </IonModal>
        </IonGrid>
      </IonContent>

      {showBookingDetails && selectedBookingId && (
        <BookingDetails
          bookingId={selectedBookingId}
          isOpen={showBookingDetails}
          hidePayment
          onClose={() => {
            setShowBookingDetails(false);
            setSelectedBookingId(undefined);
          }}
        />
      )}
    </IonPage>
  );
};

export default memo(DashboardMLS);
